import { useEffect, useState, useCallback } from 'react';
import {
  Plus, Pencil, Trash2, Loader2, Power, PowerOff, Check, Building2, Mail, Phone,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import adminApi from '../lib/adminApi';

function AnuncianteForm({ inicial, onSalvar, onCancelar, salvando }) {
  const [form, setForm] = useState(inicial || { nome: '', email: '', telefone: '', ativo: true });

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSalvar(form); }}
      className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3 mb-4">
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Nome / Empresa</label>
        <input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required data-testid="anunc-nome-input" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Telefone</label>
          <input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D9E75]" />
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer text-sm">
        <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} className="w-4 h-4 accent-[#1D9E75]" />
        <span>Ativo</span>
      </label>
      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={salvando} data-testid="salvar-anunc-btn" className="flex-1 bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl py-2 text-sm disabled:opacity-50 flex items-center justify-center gap-2">
          {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salvar
        </button>
        <button type="button" onClick={onCancelar} className="px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl py-2 text-sm">Cancelar</button>
      </div>
    </form>
  );
}

export default function AdminAnunciantes() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setLoading(true);
    try { setLista((await adminApi.get('/admin/anunciantes')).data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const handleCriar = async (form) => {
    setSalvando(true);
    try { await adminApi.post('/admin/anunciantes', form); setCriando(false); carregar(); }
    catch (err) { alert(err?.response?.data?.detail || 'Erro'); }
    finally { setSalvando(false); }
  };

  const handleAtualizar = async (form) => {
    setSalvando(true);
    try { await adminApi.patch(`/admin/anunciantes/${editando.id}`, form); setEditando(null); carregar(); }
    catch (err) { alert(err?.response?.data?.detail || 'Erro'); }
    finally { setSalvando(false); }
  };

  const toggle = async (a) => {
    await adminApi.patch(`/admin/anunciantes/${a.id}`, { ativo: !a.ativo });
    carregar();
  };

  const excluir = async (a) => {
    if (!window.confirm(`Excluir anunciante "${a.nome}"?`)) return;
    await adminApi.delete(`/admin/anunciantes/${a.id}`);
    carregar();
  };

  return (
    <AdminLayout title="Anunciantes">
      {!criando && !editando && (
        <button onClick={() => setCriando(true)} data-testid="novo-anunc-btn" className="mb-4 bg-[#1D9E75] hover:bg-[#178460] text-white font-medium rounded-xl px-4 py-2.5 text-sm flex items-center gap-2">
          <Plus size={15} /> Novo anunciante
        </button>
      )}

      {criando && <AnuncianteForm onSalvar={handleCriar} onCancelar={() => setCriando(false)} salvando={salvando} />}
      {editando && <AnuncianteForm inicial={editando} onSalvar={handleAtualizar} onCancelar={() => setEditando(null)} salvando={salvando} />}

      {loading ? (
        <div className="py-12 text-center text-gray-400 flex items-center justify-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Carregando...
        </div>
      ) : lista.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center">
          <Building2 size={28} className="text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Nenhum anunciante cadastrado.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 divide-y divide-gray-100 overflow-hidden">
          {lista.map((a) => (
            <div key={a.id} data-testid={`anunc-${a.id}`} className="p-4 hover:bg-gray-50 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-[#E1F5EE] flex items-center justify-center text-[#1D9E75] flex-shrink-0">
                  <Building2 size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold truncate ${a.ativo ? 'text-gray-900' : 'text-gray-400'}`}>{a.nome}</p>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    {a.email && <span className="text-xs text-gray-500 flex items-center gap-1"><Mail size={11} />{a.email}</span>}
                    {a.telefone && <span className="text-xs text-gray-500 flex items-center gap-1"><Phone size={11} />{a.telefone}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => toggle(a)} className={`p-1.5 rounded-lg ${a.ativo ? 'text-[#1D9E75] hover:bg-[#E1F5EE]' : 'text-gray-400 hover:bg-gray-100'}`}>
                  {a.ativo ? <Power size={14} /> : <PowerOff size={14} />}
                </button>
                <button onClick={() => setEditando(a)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Pencil size={14} /></button>
                <button onClick={() => excluir(a)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
